import { Component } from '@angular/core';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { currentDataProject } from 'src/app/services/data-project/models/data-project.model';
import * as XLSX from 'xlsx';
import { RoyaltiesDataLoader } from './data-loaders/royalties-data-loader';

@Component({
    selector: 'app-royalties',
    templateUrl: './royalties.component.html',
    styleUrls: ['./royalties.component.scss'],
})
export class RoyaltiesComponent {
    isExporting = false;
    reportDateFrom: string;
    reportDateTo: string;

    private readonly gcpProjectId: GcpProjectId = currentDataProject().gcpProjectId;

    constructor(private queryCache: QueryCacheService) {
        const now = new Date();
        const pad = (n: number) => String(n).padStart(2, '0');
        const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);
        const year = firstDay.getFullYear();
        // Months are 0-indexed in JS Date, so we add 1 for correct formatting
        const month = firstDay.getMonth() + 1;
        this.reportDateFrom = `${year}-${pad(month)}-${pad(firstDay.getDate())}`;
        this.reportDateTo = `${year}-${pad(month)}-${pad(lastDay.getDate())}`;
    }

    /** Downloads the royalties (tantiemy) Excel report for the previous calendar month. */
    async downloadRoyaltiesReport(): Promise<void> {
        if (this.isExporting) return;
        this.isExporting = true;

        try {
            await this.generateRoyaltiesReport();
        } catch (err) {
            throw new Error(`Failed to generate report. Cause: ${err}`);
        } finally {
            this.isExporting = false;
        }
    }

    /**
     * Generates the royalties report to an .xlsx file from RoyaltiesDataLoader fetched data.
     */
    async generateRoyaltiesReport(): Promise<void> {
        const { timePerGame, contentToTotal } =
            await RoyaltiesDataLoader.fetchData(
                this.gcpProjectId,
                this.queryCache,
                this.reportDateFrom,
                this.reportDateTo
            );

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(
            wb,
            XLSX.utils.json_to_sheet(timePerGame),
            'time_per_game_minutes'
        );
        XLSX.utils.book_append_sheet(
            wb,
            XLSX.utils.json_to_sheet(contentToTotal),
            'time_game_content_to_total'
        );
        XLSX.writeFile(wb, `${this.getRoyaltiesReportTitle()}.xlsx`);
    }

    /**
     * Generates the report filename:
     *  "MOPA tntiemy raport MM-YYYY_YYYYMMDDHHMM"
     *
     * The datetime suffix indicates when the report was generated and makes it easier to detect any inconsistencies in the data.
     */
    getRoyaltiesReportTitle(): string {
        const now = new Date();
        const pad = (n: number) => String(n).padStart(2, '0');
        const [year, month] = this.reportDateFrom.split('-');
        // Months are 0-indexed in JS Date, so we add 1 for correct formatting
        const timestamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}`;

        return `MOPA tantiemy raport ${month}-${year}_${timestamp}`;
    }
}
